import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule } from '@porsche-design-system/components-angular';
import {
  BehaviorSubject,
  ReplaySubject,
  distinctUntilChanged,
  shareReplay,
  tap,
  type Observable,
} from 'rxjs';
import type { InventoryManufacturer } from '../../shared/models/api/inventory-manufacturer.model';
import { InventoryManufacturerService } from '../../shared/services/api/inventory-manufacturer.service';
import { MediaQueryService } from '../../shared/services/media-query.service';
import { InventoryManufacturerCreateComponent } from './inventory-manufacturer-create/inventory-manufacturer-create.component';
import { InventoryManufacturerDeleteComponent } from './inventory-manufacturer-delete/inventory-manufacturer-delete.component';
import { InventoryManufacturerEditComponent } from './inventory-manufacturer-edit/inventory-manufacturer-edit.component';
@Component({
  selector: 'opa-inventory-item-manufacturer-list',
  imports: [
    PorscheDesignSystemModule,
    CommonModule,
    RouterLink,
    LetDirective,
    InventoryManufacturerCreateComponent,
    InventoryManufacturerEditComponent,
    InventoryManufacturerDeleteComponent,
  ],
  templateUrl: './inventory-item-manufacturer-list.component.html',
  styleUrl: './inventory-item-manufacturer-list.component.scss',
})
export class InventoryItemManufacturerListComponent {
  readonly mediaQueryService = inject(MediaQueryService);
  private readonly inventoryManufacturerService = inject(InventoryManufacturerService);

  private readonly loading = new BehaviorSubject(false);
  private readonly inventoryManufacturer = new ReplaySubject<InventoryManufacturer | null>(1);
  readonly mediaMaxXs$ = this.mediaQueryService.max('xs');
  readonly loading$ = this.loading.pipe(distinctUntilChanged());
  readonly inventoryManufacturer$ = this.inventoryManufacturer.asObservable();

  data$ = this.getData$();
  showCreateModal = false;
  showDeleteModal = false;
  showEditModal = false;

  trackInventoryManufacturer(index: number, manufacturer: InventoryManufacturer): number {
    return manufacturer.id;
  }

  reloadData(): void {
    this.data$ = this.getData$();
  }

  private getData$(): Observable<InventoryManufacturer[]> {
    this.loading.next(true);

    return this.inventoryManufacturerService.getInventoryManufacturers().pipe(
      tap({
        next: () => this.loading.next(false),
        error: () => this.loading.next(false),
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  openDeleteModal(inventoryManufacturer: InventoryManufacturer): void {
    this.inventoryManufacturer.next(inventoryManufacturer);
    this.showDeleteModal = true;
  }

  closeDeleteModal(inventoryManufacturer?: InventoryManufacturer): void {
    this.showDeleteModal = false;
    if (inventoryManufacturer) this.reloadData();
    this.inventoryManufacturer.next(null);
  }

  openEditModal(inventoryManufacturer: InventoryManufacturer): void {
    this.inventoryManufacturer.next(inventoryManufacturer);
    this.showEditModal = true;
  }

  closeEditModal(success?: boolean): void {
    this.showEditModal = false;
    if (success) this.reloadData();
    this.inventoryManufacturer.next(null);
  }
}
